'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { Icon } from '@/ui/icons/icon/Icon'
import { Loader } from '@/ui/loaders/loader/Loader'
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Button } from 'react-aria-components'
import { useFormStatus } from 'react-dom'
import Marquee from 'react-double-marquee'
import sanitizeHtml from 'sanitize-html'

const sanitizeOptions = {
  allowedTags: ['a', 'b', 'sup', 'i', 'strong'],
  allowedAttributes: {
    a: ['href', 'class', 'title', 'target'],
  },
  allowedSchemes: ['https'],
}

export type AnnouncementBarProps = UiComponentProps & {
  /** Text that will be displayed in the banner */
  text: string

  /** An hexadecimal string for the background color */
  backgroundColor?: string | null

  /** An hexadecimal string for the text color */
  textColor?: string | null

  /** Callback when the close button is pressed */
  onClose: () => void
}

export function AnnouncementBar({
  text,
  backgroundColor,
  textColor,
  onClose,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: AnnouncementBarProps): ReactElement | null {
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const [shouldMarquee, setShouldMarquee] = useState(false)
  const { pending } = useFormStatus()
  const { t } = useTranslation(['banner'])

  const updateMarquee = () => {
    if (textRef.current && containerRef.current) {
      if (textRef.current.scrollWidth > containerRef.current.clientWidth) {
        setShouldMarquee(true)
      } else {
        setShouldMarquee(false)
      }
    }
  }

  useEffect(() => {
    updateMarquee()
    window.addEventListener('resize', updateMarquee)
    window.addEventListener('deviceorientation', updateMarquee)

    return () => {
      window.removeEventListener('resize', updateMarquee)
      window.removeEventListener('deviceorientation', updateMarquee)
    }
  }, [])

  return (
    <div
      id={id}
      className={twMerge(
        'marketing-banner relative flex w-full items-center justify-between gap-1 text-nowrap px-6 text-sm font-medium',
        className,
      )}
      data-testid={dataTestId ?? 'announcement-banner'}
      aria-label={ariaLabel}
      style={{
        color: textColor ?? 'var(--color-white)',
        backgroundColor: backgroundColor ?? 'var(--color-brand-1-800)',
      }}
    >
      <div className="m-auto max-w-[90%] overflow-hidden py-1 text-center" ref={containerRef}>
        {shouldMarquee && (
          <div className="[&>div]:overflow-x-hidden">
            <Marquee
              key={text}
              className="overflow-hidden"
              scrollWhen="always"
              direction="left"
              childMargin="0"
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(text, sanitizeOptions),
                }}
              />
            </Marquee>
          </div>
        )}
        <div
          className={twMerge(shouldMarquee ? 'invisible h-0' : 'visible', 'overflow-hidden')}
          aria-hidden={shouldMarquee}
          ref={textRef}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(text, sanitizeOptions),
          }}
        />
      </div>

      <Button className="size-4" aria-label={t('banner:close')} onPress={onClose}>
        <Icon.X className={twMerge('mx-auto', pending && 'opacity-0')} />
        {pending && (
          <Loader
            className={twMerge(
              'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
              !pending && 'opacity-0',
            )}
          />
        )}
      </Button>
    </div>
  )
}
